import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import Zoneperformbystate from "./zoneperformbystate";

const calcVariance = (current, base) => {
  if (
    base === undefined ||
    base === null ||
    base === 0 ||
    base === "-" ||
    current === undefined ||
    current === null
  )
    return "-";

  return (((current - base) * 100) / base).toFixed(2) + "%";
};

const TargetIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const ZonePerformanceTable = ({ data, title }) => {
  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Product Line",
        cell: (info) => info.getValue(),
      }),

      columnHelper.group({
        id: "retailSummary",
        header: "Retail",
        columns: [
          columnHelper.accessor("opStock", { header: "Op. Stock" }),
          columnHelper.accessor("retTgt", { header: "Month Tgt" }),
          columnHelper.accessor("retMtdTgt", { header: "Ret MTD Tgt" }),

          columnHelper.group({
            header: "Actuals",
            columns: [
              columnHelper.accessor("retMtd", { header: "Retail MTD" }),
              columnHelper.accessor("retLmtd", { header: "Retail LMTD" }),
              columnHelper.accessor("retLysm", { header: "Retail LYSM MTD" }),
              columnHelper.accessor("retTotLm", { header: "Total LM" }),
              columnHelper.accessor("retTotLysm", { header: "Total LYSM" }),
            ],
          }),

          columnHelper.accessor("retMtdAch", { header: "MTD Ach%" }),

          columnHelper.group({
            header: "Variance %",
            columns: [
              columnHelper.accessor("retVarLmtd", {
                header: "Ret LMTD",
                cell: (info) => {
                  const val = info.getValue();
                  if (val === "-") return val;

                  const num = parseFloat(val);

                  return (
                    <span
                      className={
                        num >= 0
                          ? "text-green-600 font-semibold"
                          : "text-red-500 font-semibold"
                      }
                    >
                      {val}
                    </span>
                  );
                },
              }),

              columnHelper.accessor("retVarLysm", {
                header: "Retail LYSM MTD",
                cell: (info) => {
                  const val = info.getValue();
                  if (val === "-") return val;

                  const num = parseFloat(val);

                  return (
                    <span
                      className={
                        num >= 0
                          ? "text-green-600 font-semibold"
                          : "text-red-500 font-semibold"
                      }
                    >
                      {val}
                    </span>
                  );
                },
              }),
            ],
          }),
        ],
      }),

      columnHelper.group({
        id: "offtakeSummary",
        header: "Offtake",

        columns: [
          columnHelper.group({
            header: "Actuals",
            columns: [
              columnHelper.accessor("offMtd", { header: "MTD" }),
              columnHelper.accessor("offLmtd", { header: "LMTD" }),
              columnHelper.accessor("offTotLm", { header: "Total LM" }),
            ],
          }),

          columnHelper.group({
            header: "Var %",
            columns: [
              columnHelper.accessor("offVarLmtd", {
                header: "vs LMTD",

                cell: (info) => {
                  const val = info.getValue();

                  if (val === "-") return val;

                  const num = parseFloat(val);

                  return (
                    <span
                      className={
                        num >= 0
                          ? "text-green-600 font-semibold"
                          : "text-red-500 font-semibold"
                      }
                    >
                      {val}
                    </span>
                  );
                },
              }),
            ],
          }),
        ],
      }),

      columnHelper.accessor("mtdStock", {
        header: "MTD Stock",
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mb-8 last:mb-0">
      {title && (
        <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3 pl-1">
          {title}
        </h3>
      )}

      <div className="overflow-x-auto overflow-y-auto max-h-[500px] border border-slate-200 rounded-xl shadow-sm bg-white">
        <table
          id="zonePerformanceTable"
          className="w-full min-w-max border-collapse"
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="border border-slate-300 px-1 py-2 text-center text-[11px] bg-[#234b63] text-white sticky top-0 z-10"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isTotal = row.original.name
                ?.toLowerCase()
                .includes("total");

              return (
                <tr
                  key={row.id}
                  className={
                    isTotal ? "bg-blue-100 font-bold" : "hover:bg-slate-50"
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-2 py-2 border border-slate-200 text-[11px] text-center"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ZonePerformance = ({ databylob = [], offtakedata = [] }) => {
  const productLineData = useMemo(() => {
    const order = ["HCV Trucks", "ILMCV Trucks", "CV Passenger", "SCV & PU"];

    const nameMap = {
      "HCV Trucks": "HCV Trucks",
      "ILMCV Trucks": "ILMCV",
      "CV Passenger": "CV Passenger",
      "SCV & PU": "SCV & PU",
    };

    const filtered = databylob
      .filter((item) => item.key?.toLowerCase() !== "others")
      .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

    const mapped = filtered.map((item) => {
      const off = offtakedata?.find((o) => o.key === item.key);

      const offMtdVal = off?.OFFTAKE_MTD?.value ?? null;
      const offLmtdVal = off?.OFFTAKE_LMTD?.value ?? null;

      return {
        name: nameMap[item.key] || item.key,

        opStock: item.opening_stock,
        retTgt: item.month_target,
        retMtdTgt: item.retail_mtd_target,
        retMtd: item.Retail_MTD,
        retLmtd: item.Retail_LMTD,
        retLysm: item.Retail_LYSM_MTD,
        retTotLm: item.total_retail_last_month,
        retTotLysm: item.Retail_LYSM_total,

        retMtdAch: `${item.mtd_achieve}%`,

        retVarLmtd: calcVariance(item.Retail_MTD, item.Retail_LMTD),

        retVarLysm: calcVariance(item.Retail_MTD, item.Retail_LYSM_MTD),

        offMtd: offMtdVal ?? "-",
        offLmtd: offLmtdVal ?? "-",
        offTotLm: off?.OFFTAKE_MTD_LM?.value ?? "-",

        offVarLmtd: calcVariance(offMtdVal, offLmtdVal),

        mtdStock: off?.MTD_STOCK?.value ?? "-",
      };
    });

    const totalRow = mapped.reduce(
      (acc, row) => {
        acc.opStock += row.opStock || 0;
        acc.retTgt += row.retTgt || 0;
        acc.retMtdTgt += row.retMtdTgt || 0;
        acc.retMtd += row.retMtd || 0;
        acc.retLmtd += row.retLmtd || 0;
        acc.retLysm += row.retLysm || 0;
        acc.retTotLm += row.retTotLm || 0;
        acc.retTotLysm += row.retTotLysm || 0;
        acc.offMtd += row.offMtd || 0;
        acc.offLmtd += row.offLmtd || 0;
        acc.offTotLm += row.offTotLm || 0;
        acc.mtdStock += row.mtdStock || 0;

        return acc;
      },

      {
        name: "Grand Total",

        opStock: 0,
        retTgt: 0,
        retMtdTgt: 0,
        retMtd: 0,
        retLmtd: 0,
        retLysm: 0,
        retTotLm: 0,
        retTotLysm: 0,

        offMtd: 0,
        offLmtd: 0,
        offTotLm: 0,

        mtdStock: 0,
      },
    );

    totalRow.retVarLmtd = calcVariance(totalRow.retMtd, totalRow.retLmtd);

    totalRow.retVarLysm = calcVariance(totalRow.retMtd, totalRow.retLysm);

    totalRow.offVarLmtd = calcVariance(totalRow.offMtd, totalRow.offLmtd);

    totalRow.retMtdAch =
      totalRow.retMtdTgt > 0
        ? ((totalRow.retMtd / totalRow.retMtdTgt) * 100).toFixed(2) + "%"
        : "-";

    return [...mapped, totalRow];
  }, [databylob, offtakedata]);
  return (
    <div>
      <div className="bg-white rounded-2xl w-[400px] md:w-[1200px] ml-2 p-2 shadow-sm border border-slate-200">
        <div className="flex gap-2 items-center mb-2">
          <div className="bg-[#f0f6fa] rounded-lg">
            <TargetIcon className="w-5 h-5 text-[#245c81]" />
          </div>

          <h2 className="text-lg font-bold text-slate-800">
            Zone Performance - Retail & Offtake
          </h2>
        </div>

        <ZonePerformanceTable
          title="By Product Line (PL)"
          data={productLineData}
        />
      </div>
    </div>
  );
};

export default ZonePerformance;
